import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import FileUploader from "@/components/FileUploader";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Plus, Edit, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", published: true, seo_title: "", meta_description: "" };

export default function SuperBlogs() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(empty);

  const load = async () => { const { data } = await api.get("/blogs", { params: { published_only: false } }); setItems(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setF(empty); setEdit(null); setOpen(true); };
  const openEdit = (b) => { setF({ ...b, tags: (b.tags || []).join(", ") }); setEdit(b.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, tags: f.tags.split(",").map(x => x.trim()).filter(Boolean) };
      if (edit) await api.put(`/blogs/${edit}`, payload);
      else await api.post("/blogs", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/blogs/${id}`); load();
  };

  return (
    <DashLayout variant="super" title="Blog Posts">
      <div className="flex justify-between mb-6">
        <div className="text-sm text-slate-500">{items.length} posts</div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-blog-btn"><Plus className="w-4 h-4 mr-2" />New Post</Button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Slug</TableHead><TableHead>Author</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((b) => (
              <TableRow key={b.id} data-testid={`row-blog-${b.id}`}>
                <TableCell className="font-semibold">{b.title}</TableCell>
                <TableCell className="font-mono text-xs">{b.slug}</TableCell>
                <TableCell>{b.author}</TableCell>
                <TableCell>{b.published ? "Published" : "Draft"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`edit-blog-${b.id}`}><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(b.id)} data-testid={`delete-blog-${b.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? "Edit Post" : "New Post"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="blog-form">
            <div><Label className="mb-1 block">Title *</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="bf-title" /></div>
            <div><Label className="mb-1 block">Slug (URL) *</Label><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.replace(/\s+/g, "-").toLowerCase() })} data-testid="bf-slug" /></div>
            <div><Label className="mb-1 block">Cover Image</Label>
              <div className="flex items-center gap-3">
                {f.cover_image ? (
                  <div className="relative">
                    <img
                      src={f.cover_image}
                      alt="cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      className="h-20 w-32 object-cover rounded border border-slate-200 bg-slate-50"
                    />
                    <button
                      type="button"
                      onClick={() => setF({ ...f, cover_image: "" })}
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5"
                      data-testid="bf-cover-clear"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="h-20 w-32 border border-dashed border-slate-300 rounded flex items-center justify-center text-xs text-slate-400">No cover</div>
                )}
                <FileUploader folder="blogs" onUploaded={(url) => setF({ ...f, cover_image: url })} testId="bf-cover-uploader" />
              </div>
              <Input className="mt-2" placeholder="Or paste image URL" value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} data-testid="bf-cover" />
            </div>
            <div><Label className="mb-1 block">Tags (comma separated)</Label><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="bf-tags" /></div>
            <div><Label className="mb-1 block">Excerpt</Label><Textarea rows={2} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} data-testid="bf-excerpt" /></div>
            <div><Label className="mb-1 block">Content *</Label>
              <ReactQuill theme="snow" value={f.content} onChange={(v) => setF({ ...f, content: v })} data-testid="bf-content"
                modules={{ toolbar: [[{ header: [1,2,3,false] }], ["bold","italic","underline","strike"], [{ list: "ordered" }, { list: "bullet" }], ["blockquote","code-block","link","image"], ["clean"]] }} /></div>
            <div className="flex items-center gap-3"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="bf-published" /><Label>Published</Label></div>
            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">SEO (browser tab & search results)</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="mb-1 block text-xs">Page Title (SEO)</Label>
                  <Input value={f.seo_title || ""} onChange={(e) => setF({ ...f, seo_title: e.target.value })} placeholder="Leave blank to use post title" data-testid="bf-seo-title" />
                </div>
                <div><Label className="mb-1 block text-xs">Meta Description (SEO)</Label>
                  <Textarea rows={2} value={f.meta_description || ""} onChange={(e) => setF({ ...f, meta_description: e.target.value })} placeholder="1-2 sentences shown in Google search results" data-testid="bf-meta-desc" />
                </div>
              </div>
            </div>
            <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="bf-submit">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
